/**
 * A community folder's files (gh #5003, Workspace layout): `community.yaml` — identity, settings,
 * the join link's state and the rooms — and `rules.md`, the rules members see.
 *
 * Workspace files are untrusted input (Decision 10): every read and write goes through the
 * no-follow helpers of `lib/fs-safety.ts`, and a file is checked against the `@wayai/core` bodies
 * and the shape below before any request (Rules: Validation first). Every problem is reported at
 * once, so one run names all of them.
 *
 * Write-back is canonical, as pull writes hub and base files: the YAML is re-emitted in a fixed
 * key order and comments are not kept.
 */

import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { communityRoomCreateBody, communitySettingsBody, type CommunityRoomKind } from '@wayai/core/contracts';
import { expected } from '../lib/expected.js';
import { PATH_SAFE_ID_RULE, isPathSafeId } from '../lib/base-id.js';
import { fileHasBytes, readRealFileOrThrow, symlinkRefusal, writeFileNoFollow } from '../lib/fs-safety.js';
import { YAML_DUMP_OPTIONS } from '../lib/yaml-writer.js';
import { COMMUNITY_FILE, RULES_FILE } from '../lib/community-workspace.js';
import { UUID_RE } from '../lib/utils.js';
import { toLf } from './model.js';

/** One room in `community.yaml`. No `id` yet: push creates it. */
export interface RoomEntry {
  /** The stable local name: chosen once, kept through renames; the `client_id` derives from it. */
  key: string;
  id?: string;
  rev?: string;
  name: string;
  /** Read-only once the room exists. */
  kind: CommunityRoomKind;
  archived: boolean;
}

export interface CommunityFiles {
  /** Read-only: which community the folder is. */
  community_id: string;
  /** Read-only: there is no rename. */
  name: string;
  settings: { rev?: string; slow_mode_seconds: number; courses_label: string };
  /** `rules.md`, line endings as LF; '' for none (and for no file). */
  rules: string;
  join_link_enabled: boolean;
  join_link_rev?: string;
  rooms: RoomEntry[];
}

const TOP_KEYS = ['community_id', 'name', 'settings', 'join_link_enabled', 'join_link_rev', 'rooms'];
const SETTINGS_KEYS = ['rev', 'slow_mode_seconds', 'courses_label'];
const ROOM_KEYS = ['key', 'id', 'rev', 'name', 'kind', 'archived'];

type Doc = Record<string, unknown>;

function isMapping(value: unknown): value is Doc {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unknownKeys(doc: Doc, allowed: string[], where: string, problems: string[]): void {
  for (const key of Object.keys(doc)) {
    if (!allowed.includes(key)) problems.push(`${where}: unknown field ${JSON.stringify(key)}`);
  }
}

function optionalString(doc: Doc, key: string, where: string, problems: string[]): string | undefined {
  const value = doc[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') problems.push(`${where}: ${key} must be text`);
  return typeof value === 'string' ? value : undefined;
}

/** The first message of a failed contract parse, as one line. */
function contractProblem(error: { issues: Array<{ path: PropertyKey[]; message: string }> }): string {
  const [issue] = error.issues;
  return issue.path.length > 0 ? `${issue.path.map(String).join('.')}: ${issue.message}` : issue.message;
}

/**
 * Read and check a community folder, before any request. Throws one expected error naming every
 * problem found; a symlinked file is refused outright.
 */
export function readCommunityFolder(folder: string): CommunityFiles {
  const where = path.basename(folder);
  const yamlPath = path.join(folder, COMMUNITY_FILE);
  const text = readRealFileOrThrow(folder, yamlPath);
  if (text === null) {
    throw expected(`No ${COMMUNITY_FILE} in ${where}/: pull the community first (wayai pull communities/<community_id>).`);
  }
  const rules = toLf(readRealFileOrThrow(folder, path.join(folder, RULES_FILE)) ?? '');

  let doc: unknown;
  try {
    doc = yaml.load(text);
  } catch (err) {
    throw expected(`${where}/${COMMUNITY_FILE} is not valid YAML: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!isMapping(doc)) throw expected(`${where}/${COMMUNITY_FILE} must be a YAML mapping.`);

  const problems: string[] = [];
  unknownKeys(doc, TOP_KEYS, COMMUNITY_FILE, problems);

  const communityId = doc.community_id;
  if (typeof communityId !== 'string' || !UUID_RE.test(communityId)) {
    problems.push(`${COMMUNITY_FILE}: community_id must be the community's id (a UUID)`);
  }
  if (typeof doc.name !== 'string') problems.push(`${COMMUNITY_FILE}: name must be text`);

  const settings = isMapping(doc.settings) ? doc.settings : null;
  if (!settings) problems.push(`${COMMUNITY_FILE}: settings must be a mapping`);
  else unknownKeys(settings, SETTINGS_KEYS, `${COMMUNITY_FILE} settings`, problems);
  const settingsRev = settings ? optionalString(settings, 'rev', `${COMMUNITY_FILE} settings`, problems) : undefined;
  const slowMode = settings?.slow_mode_seconds;
  const coursesLabel = settings?.courses_label ?? '';
  if (settings && typeof slowMode !== 'number') problems.push(`${COMMUNITY_FILE} settings: slow_mode_seconds must be a number`);
  if (typeof coursesLabel !== 'string') problems.push(`${COMMUNITY_FILE} settings: courses_label must be text`);
  if (settings && typeof slowMode === 'number' && typeof coursesLabel === 'string') {
    const parsed = communitySettingsBody.safeParse({ rules, slow_mode_seconds: slowMode, courses_label: coursesLabel });
    if (!parsed.success) problems.push(`settings: ${contractProblem(parsed.error)}`);
  }

  if (typeof doc.join_link_enabled !== 'boolean') problems.push(`${COMMUNITY_FILE}: join_link_enabled must be true or false`);
  const joinLinkRev = optionalString(doc, 'join_link_rev', COMMUNITY_FILE, problems);

  const rooms = readRooms(doc.rooms, problems);

  if (problems.length > 0) {
    throw expected(`${where}/ cannot be synced:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  return {
    community_id: communityId as string,
    name: doc.name as string,
    settings: {
      ...(settingsRev === undefined ? {} : { rev: settingsRev }),
      slow_mode_seconds: slowMode as number,
      courses_label: coursesLabel as string,
    },
    rules,
    join_link_enabled: doc.join_link_enabled as boolean,
    ...(joinLinkRev === undefined ? {} : { join_link_rev: joinLinkRev }),
    rooms,
  };
}

/** The rooms list: each key and id once, keys plain names, every name and kind a valid create. */
function readRooms(raw: unknown, problems: string[]): RoomEntry[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) {
    problems.push(`${COMMUNITY_FILE}: rooms must be a list`);
    return [];
  }
  const rooms: RoomEntry[] = [];
  const keys = new Set<string>();
  const ids = new Set<string>();
  raw.forEach((entry, index) => {
    const where = `${COMMUNITY_FILE} rooms[${index}]`;
    if (!isMapping(entry)) {
      problems.push(`${where}: must be a mapping`);
      return;
    }
    unknownKeys(entry, ROOM_KEYS, where, problems);
    const key = entry.key;
    if (typeof key !== 'string' || !isPathSafeId(key)) {
      problems.push(`${where}: key must be a plain name — ${PATH_SAFE_ID_RULE}`);
      return;
    }
    const at = `${COMMUNITY_FILE} room ${key}`;
    if (keys.has(key)) problems.push(`${at}: the key is used twice`);
    keys.add(key);
    const id = optionalString(entry, 'id', at, problems);
    const rev = optionalString(entry, 'rev', at, problems);
    if (id !== undefined) {
      if (!UUID_RE.test(id)) problems.push(`${at}: id must be a room id (a UUID)`);
      else if (ids.has(id)) problems.push(`${at}: id ${id} is listed twice`);
      ids.add(id);
    } else if (rev !== undefined) {
      problems.push(`${at}: a rev without an id — remove the rev to create the room`);
    }
    if (entry.archived !== undefined && typeof entry.archived !== 'boolean') {
      problems.push(`${at}: archived must be true or false`);
    }
    const parsed = communityRoomCreateBody.safeParse({ kind: entry.kind, name: entry.name });
    if (!parsed.success) {
      problems.push(`${at}: ${contractProblem(parsed.error)}`);
      return;
    }
    rooms.push({
      key,
      ...(id === undefined ? {} : { id }),
      ...(rev === undefined ? {} : { rev }),
      name: entry.name as string,
      kind: parsed.data.kind,
      archived: entry.archived === true,
    });
  });
  return rooms;
}

/** `community.yaml` as written: a fixed key order, revs and ids only where known. */
export function renderCommunityYaml(files: CommunityFiles): string {
  const doc = {
    community_id: files.community_id,
    name: files.name,
    settings: {
      ...(files.settings.rev ? { rev: files.settings.rev } : {}),
      slow_mode_seconds: files.settings.slow_mode_seconds,
      courses_label: files.settings.courses_label,
    },
    join_link_enabled: files.join_link_enabled,
    ...(files.join_link_rev ? { join_link_rev: files.join_link_rev } : {}),
    rooms: files.rooms.map((room) => ({
      key: room.key,
      ...(room.id ? { id: room.id } : {}),
      ...(room.rev ? { rev: room.rev } : {}),
      name: room.name,
      kind: room.kind,
      ...(room.archived ? { archived: true } : {}),
    })),
  };
  return yaml.dump(doc, YAML_DUMP_OPTIONS);
}

/** `rules.md` as written: the text with one trailing newline; empty for none. */
export function renderRules(rules: string): string {
  const text = toLf(rules).replace(/\n+$/, '');
  return text === '' ? '' : `${text}\n`;
}

/**
 * Write the folder's files where they differ, no-follow. Returns the files written, by name.
 * Creates the folder (a real directory under `root`) for a first pull.
 */
export function writeCommunityFolder(root: string, folder: string, files: CommunityFiles): string[] {
  const written: string[] = [];
  for (const [name, content] of [
    [COMMUNITY_FILE, renderCommunityYaml(files)],
    [RULES_FILE, renderRules(files.rules)],
  ] as const) {
    const abs = path.join(folder, name);
    const bytes = Buffer.from(content, 'utf-8');
    if (fileHasBytes(abs, bytes)) continue;
    if (!writeFileNoFollow(root, abs, bytes)) {
      throw symlinkRefusal(path.relative(path.dirname(root), abs), 'file');
    }
    written.push(name);
  }
  return written;
}
